import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertOctagon, CheckCircle2, Loader2, Phone, Calendar, Wrench, User, MapPin } from "lucide-react";
import { toast } from "sonner";

/* v85.45 / v85.46 — Modal alert pop-up admin untuk WO dgn catatan khusus:
 *   • CANCEL (dari GCal pull atau billing cancel)
 *   • GARANSI (klaim garansi)
 *   • LANJUT (pekerjaan dilanjutkan)
 * Tidak auto-dismiss. Admin wajib tandai "Sudah Dibaca" per item; modal tertutup
 * saat semua item selesai.
 *
 * Props:
 *   trigger?: number — ubah angka ini utk force re-fetch alerts.
 */
const TYPE_META = {
  CANCEL:  { label: "Cancel",               cls: "bg-red-600 text-white" },
  GARANSI: { label: "Klaim Garansi",        cls: "bg-amber-600 text-white" },
  LANJUT:  { label: "Pekerjaan Dilanjutkan", cls: "bg-indigo-600 text-white" },
};

export default function CancelAlertModal({ trigger = 0 }) {
  const [items, setItems] = useState(null); // null = belum fetch
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState({}); // id → bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.woCancelAlerts();
      setItems(r.items || []);
    } catch (e) {
      // Silent — alert ini bukan critical path
      console.warn("[CancelAlertModal] load gagal:", e?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, trigger]);

  async function markRead(id) {
    setMarking(prev => ({ ...prev, [id]: true }));
    try {
      await api.woMarkCancelNoteRead(id);
      setItems(prev => (prev || []).filter(x => x.id !== id));
      toast.success("Ditandai sudah dibaca");
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setMarking(prev => ({ ...prev, [id]: false }));
    }
  }

  // Jangan render apa pun kalau tidak ada alert / belum fetch
  if (!items || items.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="cancel-alert-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-zinc-900 rounded-sm shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border-2 border-red-500">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-red-500 bg-red-50 dark:bg-red-950/30">
          <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <AlertOctagon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-['Manrope'] font-bold text-[16px] text-red-900 dark:text-red-100">
              Catatan Teknisi — Perlu Tindak Lanjut
            </div>
            <div className="text-[12px] text-red-700 dark:text-red-300">
              {items.length} WO dengan catatan khusus (Cancel / Garansi / Pekerjaan Dilanjutkan) — mohon dibaca & ditindaklanjuti
            </div>
          </div>
          <Badge className="bg-red-600 text-white rounded-sm text-[11px] font-mono" data-testid="cancel-alert-count">
            {items.length}
          </Badge>
        </div>

        {/* Body — scrollable list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading && items.length === 0 && (
            <div className="flex items-center justify-center py-10 text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Memuat…
            </div>
          )}
          {items.map(it => {
            const typeMeta = TYPE_META[it.cancel_note_type] || TYPE_META.CANCEL;
            return (
            <div
              key={it.id}
              className="border border-zinc-200 dark:border-zinc-700 rounded-sm p-3 bg-zinc-50 dark:bg-zinc-800/40"
              data-testid={`cancel-alert-item-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <Badge
                      className={`text-[10px] py-0 px-1.5 rounded-sm font-bold ${typeMeta.cls}`}
                      data-testid={`alert-type-${it.id}`}
                    >
                      {typeMeta.label}
                    </Badge>
                    {it.service_type && (
                      <Badge
                        variant="outline"
                        className="text-[9px] py-0 px-1.5 rounded-sm font-mono"
                      >
                        {it.service_type}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-zinc-600 dark:text-zinc-400">
                    <User className="w-3 h-3" />
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                      {it.customer_name || "—"}
                    </span>
                    {it.customer_phone && (
                      <>
                        <Phone className="w-3 h-3 ml-1" />
                        <span className="font-mono">{it.customer_phone}</span>
                      </>
                    )}
                  </div>
                  {it.address && (
                    <div className="flex items-start gap-1.5 text-[11px] text-zinc-600 dark:text-zinc-400 mt-1">
                      <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span className="leading-tight">{it.address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-zinc-600 dark:text-zinc-400 mt-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-mono">{it.scheduled_date || "—"}</span>
                    <Wrench className="w-3 h-3 ml-1" />
                    <span>{it.technician_name}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => markRead(it.id)}
                  disabled={!!marking[it.id]}
                  className="h-8 rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                  data-testid={`mark-read-btn-${it.id}`}
                >
                  {marking[it.id] ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  )}
                  Tandai Sudah Dibaca
                </Button>
              </div>
              <div
                className="mt-2 border-l-4 border-red-500 bg-white dark:bg-zinc-900 rounded-r-sm px-3 py-2 text-[13px] text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap font-mono leading-relaxed"
                data-testid={`cancel-note-text-${it.id}`}
              >
                {it.cancel_note}
              </div>
            </div>
            );
          })}
        </div>

        {/* Footer — info only */}
        <div className="px-5 py-3 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/60 text-[11px] text-zinc-500 flex items-center justify-between">
          <span>Modal tertutup otomatis saat semua item ditandai.</span>
          <span className="font-mono">v85.45</span>
        </div>
      </div>
    </div>
  );
}
